import { z } from 'zod';

/**
 * Single source of truth for the `/api/sms-test/*` request/response bodies —
 * mirrored on the API (Hono `zValidator`) and the FE (`SmsTestClient` +
 * `use-sms-chat`). This is the in-product test tool that drives the real
 * `apps/sms-agent` service against a real tenant (no Twilio): Start assembles the
 * outbound conversation, Reply feeds each patient turn, and the agent books a
 * real appointment. See `apps/api/src/core/sms-test/`.
 */

/** Operator-selected scheduling scope (locks slot search / duration). All optional. */
const schedulingScope = {
  providerId: z.string().uuid().optional(),
  locationId: z.string().uuid().optional(),
  appointmentTypeId: z.string().uuid().optional(),
  /** New-patient path (drives eligibility + the reminders block). Defaults true. */
  isNewPatient: z.boolean().optional(),
};

/**
 * Start body — a discriminated union on `mode`:
 *  - `referral`: mirror the voice outbound referral (patient + referral context
 *    derived from a referral task or document via TaskDocumentResolver).
 *  - `direct`: pick a real patient directly (minimal referral context).
 */
export const startSmsTestBodySchema = z.discriminatedUnion('mode', [
  z
    .object({
      mode: z.literal('referral'),
      taskId: z.string().uuid().optional(),
      documentId: z.string().uuid().optional(),
      ...schedulingScope,
    })
    .refine((b) => Boolean(b.taskId) !== Boolean(b.documentId), {
      message: 'Provide exactly one of taskId or documentId',
      path: ['taskId'],
    }),
  z.object({
    mode: z.literal('direct'),
    patientId: z.string().uuid(),
    ...schedulingScope,
  }),
]);

export type StartSmsTestBody = z.infer<typeof startSmsTestBodySchema>;

/** Reply body — one patient turn on an existing conversation. */
export const smsTestReplyBodySchema = z.object({
  conversationId: z.string().min(1),
  body: z.string().min(1),
});

export type SmsTestReplyBody = z.infer<typeof smsTestReplyBodySchema>;

/** Start response — the conversation id + the agent's opener message(s). */
export const smsTestStartResponseSchema = z.object({
  conversationId: z.string(),
  messages: z.array(z.string()),
  done: z.boolean(),
});

export type SmsTestStartResponse = z.infer<typeof smsTestStartResponseSchema>;

/** Reply response — the agent's message(s) and whether the conversation ended. */
export const smsTestReplyResponseSchema = z.object({
  messages: z.array(z.string()),
  done: z.boolean(),
});

export type SmsTestReplyResponse = z.infer<typeof smsTestReplyResponseSchema>;
